import type { ApiConfig } from "@/data/types/api-config";
import type { ClientConfig } from "@/features/client-config/types/client-config";
import {
  IDB_NAME,
  IDB_VERSION,
} from "@/services/storage/idb-constants/idb-constants";
import { openDB, type IDBPDatabase } from "idb";

type SnapshotCacheStats = {
  count: number;
  totalBytes: number;
};

export const IDB_OPEN_TIMEOUT_MS = 5000;

class StorageManager {
  private db: IDBPDatabase | null = null;
  private initPromise: Promise<IDBPDatabase> | null = null;
  private snapshotBlobUrlCache = new Map<string, string>();
  private snapshotUrlInflight = new Map<string, Promise<string | null>>();
  private courseImageBlobUrlCache = new Map<string, string>();
  private courseImageUrlInflight = new Map<string, Promise<string | null>>();

  /**
   * Opening IndexedDB is not free, and every WebSocket shot handler calls
   * this via configManager.getConfig(). Memoizing collapses concurrent and
   * repeated calls into a single openDB() so the shot handling path isn't
   * gated behind IDB latency on every message.
   */
  async initialize(): Promise<IDBPDatabase> {
    if (this.db) {
      return this.db;
    }
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = this.openDatabase();
    try {
      return await this.initPromise;
    } finally {
      this.initPromise = null;
    }
  }

  private async openDatabase(): Promise<IDBPDatabase> {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const timeoutPromise = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(
        () => reject(new Error("Opening IndexedDB timed out")),
        IDB_OPEN_TIMEOUT_MS
      );
    });

    try {
      this.db = await Promise.race([this.openDbConnection(), timeoutPromise]);
      return this.db;
    } catch (error) {
      if (
        !(error instanceof Error) ||
        error.message !== "Opening IndexedDB timed out"
      ) {
        console.error("Failed to initialize IndexedDB:", error);
      }
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  private async openDbConnection(): Promise<IDBPDatabase> {
    return openDB(IDB_NAME, IDB_VERSION, {
      upgrade(db, oldVersion) {
        if (!db.objectStoreNames.contains("offline-data")) {
          db.createObjectStore("offline-data");
        }
        if (!db.objectStoreNames.contains("pending-requests")) {
          db.createObjectStore("pending-requests");
        }
        if (!db.objectStoreNames.contains("app-state")) {
          db.createObjectStore("app-state");
        }
        if (oldVersion < 3) {
          if (!db.objectStoreNames.contains("auth-tokens")) {
            db.createObjectStore("auth-tokens");
          }
          if (!db.objectStoreNames.contains("auth-user")) {
            db.createObjectStore("auth-user");
          }
          if (!db.objectStoreNames.contains("auth-state")) {
            db.createObjectStore("auth-state");
          }
        }
        if (oldVersion < 4) {
          if (!db.objectStoreNames.contains("shot-snapshots")) {
            db.createObjectStore("shot-snapshots");
          }
        }
        if (oldVersion < 5) {
          if (!db.objectStoreNames.contains("claim-queue")) {
            const store = db.createObjectStore("claim-queue", {
              keyPath: "id",
            });
            store.createIndex("measurementId", "measurementId", {
              unique: true,
            });
            store.createIndex("status", "status", { unique: false });
            store.createIndex("createdAt", "createdAt", { unique: false });
          }
        }
        if (oldVersion < 6) {
          if (!db.objectStoreNames.contains("course-images")) {
            db.createObjectStore("course-images");
          }
        }
      },
    });
  }

  getDatabase(): IDBPDatabase | null {
    return this.db;
  }

  /**
   * Closes the persistent IndexedDB connection. Required before
   * `indexedDB.deleteDatabase()` calls, which otherwise block forever while
   * this connection stays open.
   */
  closeConnection(): void {
    if (this.db) {
      this.db.close();
      this.db = null;
    }
    this.initPromise = null;
  }

  async setOfflineData(key: string, data: unknown): Promise<void> {
    if (!this.db) {
      return;
    }

    try {
      await this.db.put("offline-data", data, key);
    } catch (error) {
      console.error("Failed to store offline data:", error);
    }
  }

  async getOfflineData<T = unknown>(key: string): Promise<T | null> {
    if (!this.db) {
      return null;
    }

    try {
      return (await this.db.get("offline-data", key)) as T;
    } catch (error) {
      console.error("Failed to retrieve offline data:", error);
      return null;
    }
  }

  async setAppState(key: string, state: unknown): Promise<void> {
    if (!this.db) {
      return;
    }

    try {
      await this.db.put("app-state", state, key);
    } catch (error) {
      console.error("Failed to store app state:", error);
    }
  }

  async getAppState<T = unknown>(key: string): Promise<T | null> {
    if (!this.db) {
      return null;
    }

    try {
      return (await this.db.get("app-state", key)) as T;
    } catch (error) {
      console.error("Failed to retrieve app state:", error);
      return null;
    }
  }

  async deleteAppState(key: string): Promise<void> {
    if (!this.db) {
      return;
    }

    try {
      await this.db.delete("app-state", key);
      console.log(`✅ [StorageManager] Deleted app state key: ${key}`);
    } catch (error) {
      console.error(`Failed to delete app state key ${key}:`, error);
    }
  }

  async clearAllData(): Promise<void> {
    if (!this.db) {
      return;
    }

    try {
      this.revokeAllSnapshotUrls();
      this.revokeAllCourseImageUrls();
      await this.db.clear("offline-data");
      await this.db.clear("pending-requests");
      await this.db.clear("app-state");
      await this.db.clear("shot-snapshots");
      await this.db.clear("claim-queue");
      await this.db.clear("course-images");
    } catch (error) {
      console.error("Failed to clear all data:", error);
    }
  }

  async getStorageUsage(): Promise<{ used: number; available: number }> {
    if (!this.db) {
      return { used: 0, available: 0 };
    }

    try {
      const estimate = await navigator.storage?.estimate();
      return {
        used: estimate?.usage || 0,
        available: estimate?.quota || 0,
      };
    } catch (error) {
      console.error("Failed to get storage usage:", error);
      return { used: 0, available: 0 };
    }
  }

  async getCurrentSessionId(): Promise<string | null> {
    if (!this.db) {
      return null;
    }

    try {
      return (await this.db.get("app-state", "current-session-id")) as
        | string
        | null;
    } catch (error) {
      console.error("Failed to retrieve current session ID:", error);
      return null;
    }
  }

  async setCurrentSessionId(sessionId: string): Promise<void> {
    if (!this.db) {
      return;
    }

    try {
      await this.db.put("app-state", sessionId, "current-session-id");
    } catch (error) {
      console.error("Failed to store current session ID:", error);
    }
  }

  async clearCurrentSessionId(): Promise<void> {
    if (!this.db) {
      return;
    }

    try {
      await this.db.delete("app-state", "current-session-id");
      console.log("✅ [StorageManager] Current session ID cleared from cache");
    } catch (error) {
      console.error("Failed to clear current session ID:", error);
    }
  }

  // Client configuration methods
  async setClientConfig(config: ClientConfig): Promise<void> {
    if (!this.db) {
      return;
    }

    try {
      await this.db.put("app-state", config, "client-config");
    } catch (error) {
      console.error("Failed to store client configuration:", error);
    }
  }

  async getClientConfig(): Promise<ClientConfig | null> {
    if (!this.db) {
      return null;
    }

    try {
      return (await this.db.get(
        "app-state",
        "client-config"
      )) as ClientConfig | null;
    } catch (error) {
      console.error("Failed to retrieve client configuration:", error);
      return null;
    }
  }

  async clearClientConfig(): Promise<void> {
    if (!this.db) {
      return;
    }

    try {
      await this.db.delete("app-state", "client-config");
    } catch (error) {
      console.error("Failed to clear client configuration:", error);
    }
  }

  // Client configuration backup (single previous version, not a history)
  async setClientConfigBackup(config: ClientConfig): Promise<void> {
    if (!this.db) {
      return;
    }

    try {
      await this.db.put("app-state", config, "client-config-backup");
    } catch (error) {
      console.error("Failed to store client configuration backup:", error);
    }
  }

  async getClientConfigBackup(): Promise<ClientConfig | null> {
    if (!this.db) {
      return null;
    }

    try {
      return (await this.db.get(
        "app-state",
        "client-config-backup"
      )) as ClientConfig | null;
    } catch (error) {
      console.error("Failed to retrieve client configuration backup:", error);
      return null;
    }
  }

  // API configuration methods
  async setApiConfig(config: ApiConfig): Promise<void> {
    if (!this.db) {
      return;
    }

    try {
      await this.db.put("app-state", config, "api-config");
    } catch (error) {
      console.error("Failed to store API configuration:", error);
    }
  }

  async getApiConfig(): Promise<ApiConfig | null> {
    if (!this.db) {
      return null;
    }

    try {
      return (await this.db.get("app-state", "api-config")) as ApiConfig | null;
    } catch (error) {
      console.error("Failed to retrieve API configuration:", error);
      return null;
    }
  }

  async clearApiConfig(): Promise<void> {
    if (!this.db) {
      return;
    }

    try {
      await this.db.delete("app-state", "api-config");
    } catch (error) {
      console.error("Failed to clear API configuration:", error);
    }
  }

  // Auth token methods
  async setAuthTokens(tokens: unknown): Promise<void> {
    if (!this.db) {
      return;
    }

    try {
      await this.db.put("auth-tokens", tokens, "tokens");
    } catch (error) {
      console.error("Failed to store auth tokens:", error);
    }
  }

  async getAuthTokens<T = unknown>(): Promise<T | null> {
    if (!this.db) {
      return null;
    }

    try {
      return (await this.db.get("auth-tokens", "tokens")) as T | null;
    } catch (error) {
      console.error("Failed to retrieve auth tokens:", error);
      return null;
    }
  }

  async clearAuthTokens(): Promise<void> {
    if (!this.db) {
      return;
    }

    try {
      await this.db.delete("auth-tokens", "tokens");
    } catch (error) {
      console.error("Failed to clear auth tokens:", error);
    }
  }

  // Auth user methods
  async setAuthUser(user: unknown): Promise<void> {
    if (!this.db) {
      return;
    }

    try {
      await this.db.put("auth-user", user, "user");
    } catch (error) {
      console.error("Failed to store auth user:", error);
    }
  }

  async getAuthUser<T = unknown>(): Promise<T | null> {
    if (!this.db) {
      return null;
    }

    try {
      return (await this.db.get("auth-user", "user")) as T | null;
    } catch (error) {
      console.error("Failed to retrieve auth user:", error);
      return null;
    }
  }

  async clearAuthUser(): Promise<void> {
    if (!this.db) {
      return;
    }

    try {
      await this.db.delete("auth-user", "user");
    } catch (error) {
      console.error("Failed to clear auth user:", error);
    }
  }

  // Auth state methods (for OAuth state parameter)
  async setAuthState(state: string): Promise<void> {
    if (!this.db) {
      return;
    }

    try {
      await this.db.put("auth-state", state, "state");
    } catch (error) {
      console.error("Failed to store auth state:", error);
    }
  }

  async getAuthState(): Promise<string | null> {
    if (!this.db) {
      return null;
    }

    try {
      return (await this.db.get("auth-state", "state")) as string | null;
    } catch (error) {
      console.error("Failed to retrieve auth state:", error);
      return null;
    }
  }

  async clearAuthState(): Promise<void> {
    if (!this.db) {
      return;
    }

    try {
      await this.db.delete("auth-state", "state");
    } catch (error) {
      console.error("Failed to clear auth state:", error);
    }
  }

  // Clear all auth data
  async clearAllAuthData(): Promise<void> {
    await this.clearAuthTokens();
    await this.clearAuthUser();
    await this.clearAuthState();
  }

  // Shot snapshot methods
  private cacheSnapshotBlobUrl(shotId: string, imageBlob: Blob): string {
    const existing = this.snapshotBlobUrlCache.get(shotId);
    if (existing) {
      URL.revokeObjectURL(existing);
    }
    const objectUrl = URL.createObjectURL(imageBlob);
    this.snapshotBlobUrlCache.set(shotId, objectUrl);
    return objectUrl;
  }

  /**
   * Store snapshot blob in IndexedDB and cache a single object URL per shot
   */
  async storeSnapshot(shotId: string, imageBlob: Blob): Promise<void> {
    if (!this.db) {
      throw new Error("Database not initialized");
    }

    try {
      await this.db.put("shot-snapshots", imageBlob, shotId);
      this.cacheSnapshotBlobUrl(shotId, imageBlob);
    } catch (error) {
      console.error("Failed to store snapshot:", error);
      throw error;
    }
  }

  /**
   * Get cached object URL for a snapshot, or create one from IndexedDB
   */
  async getSnapshotUrl(shotId: string): Promise<string | null> {
    const cached = this.snapshotBlobUrlCache.get(shotId);
    if (cached) {
      return cached;
    }

    if (!this.db) {
      return null;
    }

    const inflight = this.snapshotUrlInflight.get(shotId);
    if (inflight) {
      return inflight;
    }

    const loadPromise = this.loadSnapshotUrlFromDb(shotId);
    this.snapshotUrlInflight.set(shotId, loadPromise);
    try {
      return await loadPromise;
    } finally {
      this.snapshotUrlInflight.delete(shotId);
    }
  }

  private async loadSnapshotUrlFromDb(shotId: string): Promise<string | null> {
    if (!this.db) {
      return null;
    }

    try {
      const blob = (await this.db.get("shot-snapshots", shotId)) as
        | Blob
        | undefined;
      if (!blob) {
        return null;
      }

      const cachedAfterRead = this.snapshotBlobUrlCache.get(shotId);
      if (cachedAfterRead) {
        return cachedAfterRead;
      }

      return this.cacheSnapshotBlobUrl(shotId, blob);
    } catch (error) {
      console.error("Failed to retrieve snapshot:", error);
      return null;
    }
  }

  revokeSnapshotUrl(shotId: string): void {
    const url = this.snapshotBlobUrlCache.get(shotId);
    if (!url) {
      return;
    }
    URL.revokeObjectURL(url);
    this.snapshotBlobUrlCache.delete(shotId);
  }

  revokeAllSnapshotUrls(): void {
    for (const url of this.snapshotBlobUrlCache.values()) {
      URL.revokeObjectURL(url);
    }
    this.snapshotBlobUrlCache.clear();
  }

  async getSnapshotCacheStats(): Promise<SnapshotCacheStats> {
    if (!this.db) {
      return { count: 0, totalBytes: 0 };
    }

    try {
      const blobs = await this.db.getAll("shot-snapshots");
      let totalBytes = 0;

      for (const value of blobs) {
        if (value instanceof Blob) {
          totalBytes += value.size;
        }
      }

      return { count: blobs.length, totalBytes };
    } catch (error) {
      console.error("Failed to get snapshot cache stats:", error);
      return { count: 0, totalBytes: 0 };
    }
  }

  /**
   * Delete snapshot from IndexedDB and revoke its object URL
   */
  async deleteSnapshot(shotId: string): Promise<void> {
    if (!this.db) {
      return;
    }

    try {
      this.revokeSnapshotUrl(shotId);
      await this.db.delete("shot-snapshots", shotId);
    } catch (error) {
      console.error("Failed to delete snapshot:", error);
    }
  }

  /**
   * Clear all snapshots from IndexedDB and revoke cached object URLs
   */
  async clearAllSnapshots(): Promise<void> {
    if (!this.db) {
      return;
    }

    try {
      this.revokeAllSnapshotUrls();
      await this.db.clear("shot-snapshots");
      console.log("✅ [StorageManager] All snapshots cleared");
    } catch (error) {
      console.error("Failed to clear all snapshots:", error);
    }
  }

  private cacheCourseImageBlobUrl(imageUrl: string, imageBlob: Blob): string {
    const existing = this.courseImageBlobUrlCache.get(imageUrl);
    if (existing) {
      URL.revokeObjectURL(existing);
    }
    const objectUrl = URL.createObjectURL(imageBlob);
    this.courseImageBlobUrlCache.set(imageUrl, objectUrl);
    return objectUrl;
  }

  async storeCourseImage(imageUrl: string, imageBlob: Blob): Promise<void> {
    if (!this.db) {
      throw new Error("Database not initialized");
    }

    try {
      await this.db.put("course-images", imageBlob, imageUrl);
      this.cacheCourseImageBlobUrl(imageUrl, imageBlob);
    } catch (error) {
      console.error("Failed to store course image:", error);
      throw error;
    }
  }

  async getCourseImageUrl(imageUrl: string): Promise<string | null> {
    const cached = this.courseImageBlobUrlCache.get(imageUrl);
    if (cached) {
      return cached;
    }

    if (!this.db) {
      return null;
    }

    const inflight = this.courseImageUrlInflight.get(imageUrl);
    if (inflight) {
      return inflight;
    }

    const loadPromise = this.loadCourseImageUrlFromDb(imageUrl);
    this.courseImageUrlInflight.set(imageUrl, loadPromise);
    try {
      return await loadPromise;
    } finally {
      this.courseImageUrlInflight.delete(imageUrl);
    }
  }

  private async loadCourseImageUrlFromDb(
    imageUrl: string
  ): Promise<string | null> {
    if (!this.db) {
      return null;
    }

    try {
      const blob = (await this.db.get("course-images", imageUrl)) as
        | Blob
        | undefined;
      if (!blob) {
        return null;
      }

      const cachedAfterRead = this.courseImageBlobUrlCache.get(imageUrl);
      if (cachedAfterRead) {
        return cachedAfterRead;
      }

      return this.cacheCourseImageBlobUrl(imageUrl, blob);
    } catch (error) {
      console.error("Failed to retrieve course image:", error);
      return null;
    }
  }

  revokeCourseImageUrl(imageUrl: string): void {
    const url = this.courseImageBlobUrlCache.get(imageUrl);
    if (!url) {
      return;
    }
    URL.revokeObjectURL(url);
    this.courseImageBlobUrlCache.delete(imageUrl);
  }

  revokeAllCourseImageUrls(): void {
    for (const url of this.courseImageBlobUrlCache.values()) {
      URL.revokeObjectURL(url);
    }
    this.courseImageBlobUrlCache.clear();
  }

  async deleteCourseImagesExcept(keepUrl: string): Promise<void> {
    if (!this.db) {
      return;
    }

    try {
      const keys = await this.db.getAllKeys("course-images");
      await Promise.all(
        keys
          .filter((key) => key !== keepUrl)
          .map(async (key) => {
            const keyString = String(key);
            this.revokeCourseImageUrl(keyString);
            await this.db?.delete("course-images", key);
          })
      );
    } catch (error) {
      console.error("Failed to prune course images:", error);
    }
  }

  async clearAllCourseImages(): Promise<void> {
    if (!this.db) {
      return;
    }

    try {
      this.revokeAllCourseImageUrls();
      await this.db.clear("course-images");
    } catch (error) {
      console.error("Failed to clear all course images:", error);
    }
  }
}

export const storageManager = new StorageManager();
